import React, { useState } from 'react';
import { tilfoejFoedselsDag } from '../../services/firestoreService';
import { useToast } from '../shared/Toast';
import Button from '../shared/Button';
import { DANSKE_MAANEDER } from '../../utils/dateUtils';
import styles from './Form.module.scss';

const TilfoejFoedselsDagForm: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [submitting, setSubmitting] = useState(false);

  const [navn, setNavn] = useState('');
  const [dag, setDag] = useState('1');
  const [maaned, setMaaned] = useState('1');
  const [foedselsaar, setFoedselsaar] = useState('');
  const [vej, setVej] = useState('Askevej');
  const [vejnummer, setVejnummer] = useState('');
  const [oprettetAf, setOprettetAf] = useState('');

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 120 }, (_, i) => currentYear - i);

  const daysInMonth = new Date(
    currentYear,
    parseInt(maaned),
    0
  ).getDate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!navn.trim()) return;

    setSubmitting(true);
    try {
      const dato = `${String(maaned).padStart(2, '0')}-${String(dag).padStart(2, '0')}`;

      await tilfoejFoedselsDag({
        navn: navn.trim(),
        dato,
        foedselsaar: foedselsaar ? parseInt(foedselsaar) : undefined,
        type: 'foedselsdag',
        vej: vej.trim() || undefined,
        vejnummer: vejnummer.trim() || undefined,
        oprettetAf: oprettetAf.trim() || undefined,
      });

      showSuccess(`${navn.trim()}s fødselsdag er blevet tilføjet!`);
      // Reset form
      setNavn('');
      setDag('1');
      setMaaned('1');
      setFoedselsaar('');
      setVej('Askevej');
      setVejnummer('');
      setOprettetAf('');
    } catch (err) {
      console.error(err);
      showError('Noget gik galt. Prøv igen.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <div className={styles.field}>
        <label htmlFor="fd-navn" className={styles.label}>
          Navn <span className={styles.required}>*</span>
        </label>
        <input
          id="fd-navn"
          type="text"
          className={styles.input}
          value={navn}
          onChange={(e) => setNavn(e.target.value)}
          maxLength={100}
          placeholder="Fx. Mormor Else"
          required
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Fødselsdato</label>
        <div className={styles.dateRow}>
          <div className={styles.selectWrapper}>
            <label htmlFor="fd-dag" className={styles.sublabel}>Dag</label>
            <input
              id="fd-dag"
              type="number"
              className={styles.input}
              value={dag}
              min={1}
              max={daysInMonth}
              onChange={(e) => setDag(e.target.value)}
              inputMode="numeric"
            />
          </div>
          <div className={styles.selectWrapper}>
            <label htmlFor="fd-maaned" className={styles.sublabel}>Måned</label>
            <select
              id="fd-maaned"
              className={styles.select}
              value={maaned}
              onChange={(e) => setMaaned(e.target.value)}
            >
              {DANSKE_MAANEDER.map((m, i) => (
                <option key={i} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="fd-foedselsaar" className={styles.label}>
          Fødselsår <span className={styles.optional}>(valgfri)</span>
        </label>
        <select
          id="fd-foedselsaar"
          className={styles.select}
          value={foedselsaar}
          onChange={(e) => setFoedselsaar(e.target.value)}
        >
          <option value="">Vælg år (valgfri)</option>
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Adresse <span className={styles.optional}>(valgfri)</span></label>
        <div className={styles.dateRow}>
          <div className={styles.selectWrapper} style={{ flex: 2 }}>
            <label htmlFor="fd-vej" className={styles.sublabel}>Vejnavn</label>
            <input
              id="fd-vej"
              type="text"
              className={styles.input}
              value={vej}
              onChange={(e) => setVej(e.target.value)}
              placeholder="Fx. Askevej"
            />
          </div>
          <div className={styles.selectWrapper} style={{ flex: 1 }}>
            <label htmlFor="fd-vejnummer" className={styles.sublabel}>Nummer</label>
            <input
              id="fd-vejnummer"
              type="text"
              className={styles.input}
              value={vejnummer}
              onChange={(e) => setVejnummer(e.target.value)}
              placeholder="Fx. 12"
            />
          </div>
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="fd-oprettetaf" className={styles.label}>
          Dit navn <span className={styles.optional}>(valgfri)</span>
        </label>
        <input
          id="fd-oprettetaf"
          type="text"
          className={styles.input}
          value={oprettetAf}
          onChange={(e) => setOprettetAf(e.target.value)}
          placeholder="Fx. Familie Hansen"
        />
      </div>

      <Button type="submit" disabled={submitting || !navn.trim()} fullWidth>
        {submitting ? 'Tilføjer...' : 'Tilføj fødselsdag'}
      </Button>
    </form>
  );
};

export default TilfoejFoedselsDagForm;
